<template>
  <div class="sdk-partner-page w-100">
    <Wrapper>
      <!-- Chưa cấu hình gateway -->
      <b-alert v-if="!isConfigured" show variant="warning" class="mb-0">
        Chưa cấu hình <code>VUE_APP_SDK_GATEWAY</code> trong file môi trường.
        Vui lòng bổ sung biến này để sử dụng chức năng Quản lý SDK Partner.
      </b-alert>

      <!-- Cần đăng nhập gateway (không có creds env) -->
      <div v-else-if="needLogin" class="sdk-login-panel">
        <h5 class="mb-4">Đăng nhập Gateway quản trị SDK</h5>
        <form @submit.prevent="doLogin">
          <b-row>
            <b-col :cols="12" :md="5">
              <basic-input
                label="Email"
                name="loginEmail"
                placeholder="Nhập email admin"
                :value.sync="loginForm.email"
              />
            </b-col>
            <b-col :cols="12" :md="5">
              <b-form-group label="Mật khẩu">
                <b-form-input
                  v-model="loginForm.password"
                  type="password"
                  placeholder="Nhập mật khẩu"
                />
              </b-form-group>
            </b-col>
            <b-col :cols="12" :md="2" class="d-flex align-items-end">
              <b-button
                type="submit"
                class="btn btn-success mb-3 w-100"
                :disabled="loginLoading"
              >
                <b-spinner v-if="loginLoading" small class="mr-1" />
                Đăng nhập
              </b-button>
            </b-col>
          </b-row>
        </form>
      </div>

      <template v-else>
        <!-- Header + actions -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h4 class="mb-0">Quản lý SDK Partner</h4>
          <div class="d-flex">
            <b-button
              class="btn btn-light mr-2"
              @click="$router.push({ name: 'sdk_partner_stats' })"
            >
              <i class="fas fa-chart-bar mr-1" />Thống kê
            </b-button>
            <b-button
              class="btn btn-light mr-2"
              @click="$router.push({ name: 'sdk_partner_request_logs' })"
            >
              <i class="fas fa-search mr-1" />Tra cứu Request
            </b-button>
            <b-button class="btn btn-success mr-2" @click="openCreate">
              <span class="svg-icon">
                <inline-svg src="/media/svg/icons/Neolex/Basic/plus.svg" />
              </span>
              Tạo Partner
            </b-button>
            <b-button class="btn btn-light" :disabled="loading" @click="loadData">
              <b-spinner v-if="loading" small class="mr-1" />
              <span v-else class="svg-icon">
                <inline-svg src="/media/svg/icons/Neolex/Basic/refresh-cw.svg" />
              </span>
              Làm mới
            </b-button>
          </div>
        </div>

        <!-- Summary bar -->
        <div class="sdk-summary-bar mb-4">
          <div class="sdk-summary-item is-active">
            <span class="sdk-summary-count">{{ summary.active }}</span>
            <span class="sdk-summary-label">Active</span>
          </div>
          <div class="sdk-summary-item is-expiring">
            <span class="sdk-summary-count">{{ summary.expiring }}</span>
            <span class="sdk-summary-label">Expiring Soon</span>
          </div>
          <div class="sdk-summary-item is-expired">
            <span class="sdk-summary-count">{{ summary.expired }}</span>
            <span class="sdk-summary-label">Expired</span>
          </div>
          <div class="sdk-summary-item is-revoked">
            <span class="sdk-summary-count">{{ summary.revoked }}</span>
            <span class="sdk-summary-label">Revoked</span>
          </div>
          <div class="sdk-summary-item is-requests">
            <span class="sdk-summary-count">{{ summary.requests.toLocaleString('vi-VN') }}</span>
            <span class="sdk-summary-label">Tổng request</span>
          </div>
        </div>

        <!-- Filters -->
        <b-row>
          <b-col :cols="12" :md="4">
            <basic-input
              label="Tìm kiếm"
              name="search"
              placeholder="Tìm theo tên partner"
              :value.sync="filter.search"
            />
          </b-col>
          <b-col :cols="12" :md="4">
            <b-form-group label="Trạng thái">
              <b-form-select v-model="filter.status" :options="statusOptions" />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Table -->
        <template-table
          :column="column"
          :data="pagedData"
          :paging="paging"
          :tableAction="true"
          :selectAction="false"
          :searchAction="false"
          :pagingAction="false"
        >
          <template v-slot:body="{ item, index }">
            <td>{{ index + 1 }}</td>
            <td>
              <span class="font-weight-bold">{{ item.partnerName }}</span>
            </td>
            <td><code>{{ item.apiKeyPrefix }}</code></td>
            <td>
              <div v-for="b in (item.bundleIds || [])" :key="b" class="text-muted small">
                {{ b }}
              </div>
              <span v-if="!item.bundleIds || !item.bundleIds.length" class="text-muted">—</span>
            </td>
            <td>
              <b-badge
                v-for="f in (item.features || [])"
                :key="f"
                variant="light"
                class="mr-1"
              >{{ f }}</b-badge>
            </td>
            <td>{{ formatDate(item.expiresAt) }}</td>
            <td>{{ item.meetingsUsed || 0 }} / {{ item.maxMeetings ? item.maxMeetings : '∞' }}</td>
            <td>
              <span class="font-weight-bold">{{ (item.totalRequests || 0).toLocaleString('vi-VN') }}</span>
            </td>
            <td>
              <b-badge :variant="statusOf(item).variant">
                {{ statusOf(item).label }}
              </b-badge>
            </td>
          </template>
          <template v-slot:action="{ item }">
            <div class="d-flex">
              <b-button
                size="sm"
                variant="outline-primary"
                class="mr-1"
                title="Xem log & usage"
                @click="goLogs(item)"
              >
                <i class="fas fa-chart-line" />
              </b-button>
              <b-button
                size="sm"
                variant="outline-info"
                class="mr-1"
                title="Chia sẻ thông tin tích hợp"
                @click="sharePartner(item)"
              >
                <i class="fas fa-share-alt" />
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                class="mr-1"
                title="Sửa"
                @click="openEdit(item)"
              >
                <i class="fas fa-pen" />
              </b-button>
              <b-button
                v-if="item.isActive"
                size="sm"
                variant="outline-warning"
                class="mr-1"
                title="Rotate key"
                @click="confirmRotate(item)"
              >
                <i class="fas fa-sync-alt" />
              </b-button>
              <b-button
                v-if="item.isActive"
                size="sm"
                variant="outline-danger"
                title="Revoke"
                @click="confirmRevoke(item)"
              >
                <i class="fas fa-ban" />
              </b-button>
              <b-button
                v-else
                size="sm"
                variant="outline-success"
                title="Kích hoạt lại"
                @click="confirmReactivate(item)"
              >
                <i class="fas fa-check" />
              </b-button>
            </div>
          </template>
        </template-table>
      </template>
    </Wrapper>

    <!-- Create / Edit modal -->
    <b-modal
      id="sdk-partner-form"
      :title="formMode === 'create' ? 'Tạo Partner mới' : 'Sửa Partner'"
      size="lg"
      centered
      hide-footer
      @hide="resetForm"
    >
      <b-overlay :show="formLoading">
        <form @submit.prevent="submitForm">
          <b-row>
            <b-col :cols="12" :md="6">
              <basic-input
                :required="true"
                label="Tên Partner"
                name="partnerName"
                placeholder="Nhập tên partner"
                :value.sync="form.partnerName"
              />
            </b-col>
            <b-col :cols="12" :md="6">
              <b-form-group label="Ngày hết hạn *">
                <date-picker
                  v-model="form.expiresAt"
                  input-class="form-control"
                  type="date"
                  format="DD/MM/YYYY"
                  value-type="date"
                  :clearable="false"
                  style="width: 100%"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Bundle IDs (mỗi dòng 1 id, vd: com.partner.app)">
            <b-form-textarea
              v-model="form.bundleIdsText"
              rows="2"
              placeholder="com.partner.app&#10;com.partner.app.dev"
            />
          </b-form-group>

          <b-form-group label="Tính năng *">
            <b-form-checkbox-group
              v-model="form.features"
              :options="featureOptions"
            />
          </b-form-group>

          <b-form-group label="Gói quyền lợi (bundle)">
            <b-form-select v-model="form.packageId" :options="packageOptions" />
            <small class="text-muted">
              User mới của đối tác được kích hoạt gói này khi đăng nhập; Khám từ xa / Đặt khám bị giới hạn
              theo số lượt trong gói. Để trống = không dùng gói. Đổi gói chỉ áp dụng cho user chưa có gói.
            </small>
          </b-form-group>

          <b-form-group label="Tiện ích hiển thị trên Hub (mặc định TẮT)">
            <b-form-checkbox-group
              v-model="form.enabledUtilities"
              :options="utilityOptions"
              stacked
            />
            <small class="text-muted">
              Bật tiện ích nào thì đối tác mới thấy trên màn Hub. Chưa bật = ẩn (default inactive).
              Tiện ích vẫn cần Tính năng tương ứng ở trên.
            </small>
          </b-form-group>

          <b-row>
            <b-col :cols="12" :md="4">
              <b-form-group label="Max Users">
                <b-form-checkbox v-model="unlimited.users" class="sdk-unlimited-check mb-1" @change="onUnlimited('users')">
                  Không giới hạn
                </b-form-checkbox>
                <b-form-input
                  v-if="!unlimited.users"
                  v-model.number="form.maxUsers"
                  type="number"
                  min="1"
                />
                <div v-else class="sdk-unlimited-hint">∞ Không giới hạn</div>
              </b-form-group>
            </b-col>
            <b-col :cols="12" :md="4">
              <b-form-group label="Max Meetings / tháng">
                <b-form-checkbox v-model="unlimited.meetings" class="sdk-unlimited-check mb-1" @change="onUnlimited('meetings')">
                  Không giới hạn
                </b-form-checkbox>
                <b-form-input
                  v-if="!unlimited.meetings"
                  v-model.number="form.maxMeetings"
                  type="number"
                  min="1"
                />
                <div v-else class="sdk-unlimited-hint">∞ Không giới hạn</div>
              </b-form-group>
            </b-col>
            <b-col :cols="12" :md="4">
              <b-form-group label="Max Webinars / tháng">
                <b-form-checkbox v-model="unlimited.webinars" class="sdk-unlimited-check mb-1" @change="onUnlimited('webinars')">
                  Không giới hạn
                </b-form-checkbox>
                <b-form-input
                  v-if="!unlimited.webinars"
                  v-model.number="form.maxWebinars"
                  type="number"
                  min="1"
                />
                <div v-else class="sdk-unlimited-hint">∞ Không giới hạn</div>
              </b-form-group>
            </b-col>
          </b-row>
          <div class="text-muted small mb-3">
            <i class="fas fa-info-circle mr-1" />“Không giới hạn” = không chặn theo số lượng (lưu giá trị 0).
          </div>

          <b-form-group label="Access Token Secret (HS256)">
            <b-input-group>
              <b-form-input
                v-model="form.accessTokenSecret"
                autocomplete="off"
                :placeholder="editingPartner && editingPartner.hasAccessTokenSecret
                  ? 'Đã cấu hình — để trống nếu giữ nguyên'
                  : 'Bí mật để verify token end-user của partner'"
              />
              <b-input-group-append>
                <b-button
                  v-if="editingPartner && editingPartner.hasAccessTokenSecret"
                  variant="outline-info"
                  @click="revealSecret"
                >
                  <i class="fas fa-eye mr-1" />Xem
                </b-button>
                <b-button variant="outline-secondary" @click="generateSecret">
                  <i class="fas fa-random mr-1" />Tạo ngẫu nhiên
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <small class="text-muted">
              Partner ký token end-user bằng secret này; gateway verify chữ ký khi exchange-token.
              Bỏ trống khi tạo = chỉ kiểm hạn (dev). Sản phẩm nên bật bắt buộc.
            </small>
          </b-form-group>

          <b-form-group
            v-if="editingPartner && editingPartner.hasAccessTokenSecret"
            label="Tạo token mẫu (gửi đối tác)"
            class="border rounded p-3 bg-light"
          >
            <b-row>
              <b-col md="4" class="mb-2">
                <b-form-input v-model="signForm.phone" placeholder="SĐT (bắt buộc)" />
              </b-col>
              <b-col md="4" class="mb-2">
                <b-form-input v-model="signForm.sub" placeholder="sub — id user (tùy)" />
              </b-col>
              <b-col md="4" class="mb-2">
                <b-form-input v-model="signForm.name" placeholder="Tên (tùy)" />
              </b-col>
            </b-row>
            <b-button type="button" variant="outline-primary" size="sm" @click="signSampleToken">
              <i class="fas fa-key mr-1" />Ký token
            </b-button>
            <template v-if="signedToken">
              <b-form-textarea
                v-model="signedToken"
                readonly
                rows="3"
                class="mt-2"
                @focus="$event.target.select()"
              />
              <small class="text-muted">
                Copy token này gửi đối tác (mặc định hết hạn sau 1 giờ). Gateway ký bằng secret của partner —
                secret không rời server.
              </small>
            </template>
          </b-form-group>

          <b-form-group label="NPM_TOKEN (đọc registry — cấp cho đối tác)">
            <b-form-input v-model="form.npmToken" autocomplete="off" placeholder="ghp_… token đọc GitHub Packages" />
            <small class="text-muted">Lưu để quản lý / cấp lại cho đối tác (dán vào .npmrc — xem doc §4A).</small>
          </b-form-group>

          <b-form-group label="Ghi chú">
            <b-form-textarea v-model="form.note" rows="2" placeholder="Ghi chú nội bộ về đối tác…" />
          </b-form-group>

          <div v-if="formError" class="text-danger mb-3">{{ formError }}</div>

          <div class="d-flex justify-content-end">
            <b-button class="btn mr-3" type="button" @click="$bvModal.hide('sdk-partner-form')">
              Hủy
            </b-button>
            <b-button class="btn btn-success" type="submit">
              <span class="svg-icon">
                <inline-svg src="/media/svg/icons/Neolex/Basic/save.svg" />
              </span>
              {{ formMode === 'create' ? 'Tạo' : 'Lưu' }}
            </b-button>
          </div>
        </form>
      </b-overlay>
    </b-modal>

    <!-- API key one-time dialog -->
    <b-modal
      id="sdk-apikey-dialog"
      title="API Key"
      size="md"
      centered
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      @hide="clearApiKey"
    >
      <b-alert show variant="danger" class="mb-3">
        <i class="fas fa-exclamation-triangle mr-1" />
        Key này chỉ hiển thị <strong>1 lần duy nhất</strong>. Hãy sao chép và
        lưu lại ngay — hệ thống không lưu key dạng plaintext.
      </b-alert>
      <div class="sdk-apikey-box">
        <code>{{ apiKey }}</code>
      </div>
      <div class="d-flex justify-content-between mt-3">
        <b-button variant="primary" @click="copyKey">
          <i class="fas fa-copy mr-1" />{{ copied ? 'Đã sao chép' : 'Sao chép' }}
        </b-button>
        <b-button variant="secondary" @click="$bvModal.hide('sdk-apikey-dialog')">
          Đã lưu, đóng lại
        </b-button>
      </div>
    </b-modal>

    <!-- Confirm dialog (revoke / reactivate / rotate) -->
    <b-modal id="sdk-confirm" :title="confirm.title" size="sm" centered hide-footer>
      <div class="mb-4" v-html="confirm.message"></div>
      <div class="d-flex justify-content-end">
        <b-button class="btn mr-3" @click="$bvModal.hide('sdk-confirm')">Hủy</b-button>
        <b-button :variant="confirm.variant" :disabled="confirm.loading" @click="runConfirm">
          <b-spinner v-if="confirm.loading" small class="mr-1" />
          {{ confirm.okText }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import SdkPartnerService, { SDK_FEATURES } from './services/sdk-partner.service';

const BUNDLE_ID_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/;
const EXPIRING_DAYS = 30;

export default {
  name: 'sdk_partner',
  components: {
    'date-picker': DatePicker,
  },
  data() {
    return {
      isConfigured: SdkPartnerService.isConfigured(),
      salePackages: [],
      needLogin: false,
      loading: false,
      loginLoading: false,
      loginForm: { email: '', password: '' },
      partners: [],
      filter: { search: '', status: 'all' },
      statusOptions: [
        { value: 'all', text: 'Tất cả' },
        { value: 'active', text: 'Active' },
        { value: 'expiring', text: 'Expiring Soon' },
        { value: 'expired', text: 'Expired' },
        { value: 'revoked', text: 'Revoked' },
      ],
      column: [
        { key: 'stt', label: 'Stt' },
        { key: 'partnerName', label: 'Partner' },
        { key: 'apiKeyPrefix', label: 'API Key Prefix' },
        { key: 'bundleIds', label: 'Bundle IDs' },
        { key: 'features', label: 'Features' },
        { key: 'expiresAt', label: 'Hết hạn' },
        { key: 'meetings', label: 'Meetings' },
        { key: 'requests', label: 'Số request' },
        { key: 'status', label: 'Trạng thái' },
      ],
      paging: { page: 1, pageSize: 100, total: 0 },
      featureOptions: SDK_FEATURES,
      // Keep in sync with PRODIAB_UTILITIES in the SDK (src/hub/utilities.ts).
      utilityOptions: [
        { text: 'Khám từ xa', value: 'telemedicine' },
        { text: 'Đặt khám (tại phòng khám)', value: 'at_clinic' },
        { text: 'Sự kiện / Webinar (cần tính năng Webinar)', value: 'webinar' },
        { text: 'Mua thuốc (đơn thuốc)', value: 'medicine' },
        { text: 'Đặt xét nghiệm', value: 'lab_test' },
        { text: 'Lịch hẹn (lịch sử đặt khám)', value: 'history' },
        { text: 'Quyền lợi của tôi (gói bundle)', value: 'bundle' },
        { text: 'Ưu đãi / Voucher', value: 'voucher' },
        { text: 'Trợ lý AI (cần tính năng Chat)', value: 'chat' },
      ],
      formMode: 'create',
      formLoading: false,
      formError: '',
      editingId: null,
      editingPartner: null,
      form: this.defaultForm(),
      signForm: { phone: '', sub: '', name: '' },
      signedToken: '',
      unlimited: { users: false, meetings: false, webinars: false },
      apiKey: '',
      copied: false,
      confirm: {
        title: '',
        message: '',
        okText: 'Xác nhận',
        variant: 'danger',
        loading: false,
        action: null,
        partner: null,
      },
    };
  },
  computed: {
    packageOptions() {
      const opts = [{ value: '', text: '— Không dùng gói —' }];
      this.salePackages.forEach((p) => opts.push({ value: p.id, text: p.name || p.id }));
      const current = this.form && this.form.packageId;
      if (current && !this.salePackages.some((p) => p.id === current)) {
        opts.push({ value: current, text: `${current} (không còn trong danh sách)` });
      }
      return opts;
    },
    filteredPartners() {
      const q = (this.filter.search || '').trim().toLowerCase();
      return this.partners.filter((p) => {
        if (q && !(p.partnerName || '').toLowerCase().includes(q)) return false;
        if (this.filter.status !== 'all') {
          return this.statusOf(p).key === this.filter.status;
        }
        return true;
      });
    },
    pagedData() {
      return this.filteredPartners;
    },
    summary() {
      const s = { active: 0, expiring: 0, expired: 0, revoked: 0, requests: 0 };
      this.partners.forEach((p) => {
        const key = this.statusOf(p).key;
        if (s[key] !== undefined) s[key] += 1;
        s.requests += p.totalRequests || 0;
      });
      return s;
    },
  },
  created() {
    if (this.isConfigured) this.loadData();
  },
  methods: {
    defaultForm() {
      return {
        partnerName: '',
        bundleIdsText: '',
        features: [],
        expiresAt: null,
        maxUsers: 100,
        maxMeetings: 500,
        maxWebinars: 50,
        accessTokenSecret: '',
        npmToken: '',
        note: '',
        enabledUtilities: [],
        packageId: '',
      };
    },
    async loadSalePackages() {
      if (this.salePackages.length) return;
      try {
        const res = await SdkPartnerService.getSalePackages();
        this.salePackages = Array.isArray(res) ? res : (res && res.data) || [];
      } catch (e) {
        this.$toastr.w({ title: 'Không tải được danh sách gói', msg: 'Có thể chọn lại sau.' });
      }
    },
    generateSecret() {
      const bytes = new Uint8Array(24);
      (window.crypto || window.msCrypto).getRandomValues(bytes);
      this.form.accessTokenSecret = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    },
    async revealSecret() {
      if (!this.editingId) return;
      try {
        const res = await SdkPartnerService.getSecret(this.editingId);
        const secret = res && (res.accessTokenSecret
          || (res.data && res.data.accessTokenSecret));
        if (secret) {
          this.form.accessTokenSecret = secret;
          this.$toastr.s({ title: 'Đã hiện secret', msg: 'Nhớ copy & lưu an toàn.' });
        } else {
          this.$toastr.w({ title: 'Chưa có secret', msg: 'Partner này chưa đặt Access Token Secret.' });
        }
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không lấy được secret.' });
      }
    },
    async signSampleToken() {
      if (!this.editingId) return;
      if (!this.signForm.phone || !this.signForm.phone.trim()) {
        this.$toastr.w({ title: 'Thiếu SĐT', msg: 'Nhập số điện thoại end-user để ký token.' });
        return;
      }
      try {
        const body = {
          phone: this.signForm.phone.trim(),
          sub: (this.signForm.sub || '').trim() || undefined,
          name: (this.signForm.name || '').trim() || undefined,
        };
        const res = await SdkPartnerService.signToken(this.editingId, body);
        const token = res && (res.token || (res.data && res.data.token));
        if (token) {
          this.signedToken = token;
          this.$toastr.s({ title: 'Đã ký token', msg: 'Copy token gửi đối tác.' });
        } else {
          this.$toastr.e({ title: 'Lỗi', msg: 'Không nhận được token.' });
        }
      } catch (e) {
        const code = e && e.response && e.response.data && e.response.data.error;
        this.$toastr.e({
          title: 'Lỗi ký token',
          msg: code === 'PartnerHasNoSecret'
            ? 'Partner chưa có secret — hãy đặt & lưu secret trước.'
            : (code || 'Không ký được token.'),
        });
      }
    },
    async loadData() {
      this.loading = true;
      this.$store.commit('context/setLoading', true);
      try {
        const params = {};
        if (this.filter.search) params.search = this.filter.search;
        const res = await SdkPartnerService.listPartners(params);
        const list = Array.isArray(res) ? res : res.data || [];
        // BR-004: mặc định sắp xếp theo createdAt DESC
        this.partners = [...list].sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
        );
        this.needLogin = false;
      } catch (error) {
        if (error && error.message === 'SDK_ADMIN_NO_TOKEN') {
          this.needLogin = true;
        } else {
          this.toastError(error);
        }
      } finally {
        this.loading = false;
        this.$store.commit('context/setLoading', false);
      }
    },
    async doLogin() {
      if (!this.loginForm.email || !this.loginForm.password) return;
      this.loginLoading = true;
      try {
        await SdkPartnerService.login(this.loginForm.email, this.loginForm.password);
        this.needLogin = false;
        this.loginForm.password = '';
        await this.loadData();
      } catch (error) {
        this.toastError(error);
      } finally {
        this.loginLoading = false;
      }
    },
    statusOf(partner) {
      if (!partner.isActive) {
        return { key: 'revoked', label: 'Revoked', variant: 'danger' };
      }
      const now = this.$moment();
      const exp = this.$moment(partner.expiresAt);
      if (exp.isValid() && exp.isBefore(now)) {
        return { key: 'expired', label: 'Expired', variant: 'warning' };
      }
      if (exp.isValid() && exp.diff(now, 'days') <= EXPIRING_DAYS) {
        return { key: 'expiring', label: 'Expiring Soon', variant: 'info' };
      }
      return { key: 'active', label: 'Active', variant: 'success' };
    },
    formatDate(date) {
      if (!date) return '—';
      const m = this.$moment(date);
      return m.isValid() ? m.format('DD/MM/YYYY') : '—';
    },
    parseBundleIds() {
      return (this.form.bundleIdsText || '')
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter((s) => s.length);
    },
    openCreate() {
      this.formMode = 'create';
      this.editingId = null;
      this.editingPartner = null;
      this.form = this.defaultForm();
      this.unlimited = { users: false, meetings: false, webinars: false };
      this.signForm = { phone: '', sub: '', name: '' };
      this.signedToken = '';
      this.formError = '';
      this.loadSalePackages();
      this.$bvModal.show('sdk-partner-form');
    },
    openEdit(partner) {
      this.formMode = 'edit';
      this.editingId = partner.id;
      this.editingPartner = partner;
      this.form = {
        partnerName: partner.partnerName || '',
        bundleIdsText: (partner.bundleIds || []).join('\n'),
        features: [...(partner.features || [])],
        enabledUtilities: [...(partner.enabledUtilities || [])],
        expiresAt: partner.expiresAt ? this.$moment(partner.expiresAt).toDate() : null,
        maxUsers: partner.maxUsers || 0,
        maxMeetings: partner.maxMeetings || 0,
        maxWebinars: partner.maxWebinars || 0,
        accessTokenSecret: '', // write-only — never populated; empty = giữ nguyên
        npmToken: partner.npmToken || '',
        note: partner.note || '',
        packageId: partner.packageId || '',
      };
      this.signForm = { phone: '', sub: '', name: '' };
      this.signedToken = '';
      // limit === 0 means "unlimited" (gateway convention).
      this.unlimited = {
        users: (partner.maxUsers || 0) === 0,
        meetings: (partner.maxMeetings || 0) === 0,
        webinars: (partner.maxWebinars || 0) === 0,
      };
      this.formError = '';
      this.loadSalePackages();
      this.$bvModal.show('sdk-partner-form');
    },
    onUnlimited(field) {
      const key = { users: 'maxUsers', meetings: 'maxMeetings', webinars: 'maxWebinars' }[field];
      const def = { users: 100, meetings: 500, webinars: 50 }[field];
      if (this.unlimited[field]) this.form[key] = 0;
      else if (!this.form[key] || this.form[key] < 1) this.form[key] = def;
    },
    validateForm() {
      this.formError = '';
      if (!this.form.partnerName || !this.form.partnerName.trim()) {
        this.formError = 'Vui lòng nhập tên Partner.';
        return null;
      }
      if (!this.form.expiresAt) {
        this.formError = 'Vui lòng chọn ngày hết hạn.';
        return null;
      }
      if (this.formMode === 'create' && this.$moment(this.form.expiresAt).isBefore(this.$moment(), 'day')) {
        this.formError = 'Ngày hết hạn phải sau ngày hiện tại.';
        return null;
      }
      if (!this.form.features.length) {
        this.formError = 'Phải chọn ít nhất một tính năng.';
        return null;
      }
      const bundleIds = this.parseBundleIds();
      const seen = new Set();
      for (const b of bundleIds) {
        if (seen.has(b)) {
          this.formError = `Bundle ID bị trùng: ${b}`;
          return null;
        }
        seen.add(b);
        if (!BUNDLE_ID_REGEX.test(b)) {
          this.formError = `Bundle ID không đúng định dạng: ${b}`;
          return null;
        }
      }
      // Effective limits — "Không giới hạn" (checkbox) sends 0 (gateway = unlimited).
      const maxUsers = this.unlimited.users ? 0 : Number(this.form.maxUsers) || 0;
      const maxMeetings = this.unlimited.meetings ? 0 : Number(this.form.maxMeetings) || 0;
      const maxWebinars = this.unlimited.webinars ? 0 : Number(this.form.maxWebinars) || 0;

      // A bounded limit must be a positive number (otherwise pick "Không giới hạn").
      const bounded = [
        { on: !this.unlimited.users, v: maxUsers, label: 'Max Users' },
        { on: !this.unlimited.meetings, v: maxMeetings, label: 'Max Meetings' },
        { on: !this.unlimited.webinars, v: maxWebinars, label: 'Max Webinars' },
      ];
      for (const b of bounded) {
        if (b.on && b.v < 1) {
          this.formError = `${b.label}: nhập số ≥ 1 hoặc chọn “Không giới hạn”.`;
          return null;
        }
      }

      // BR-011: không được giảm limit xuống dưới mức đã dùng (chỉ khi sửa; bỏ qua nếu không giới hạn)
      if (this.formMode === 'edit' && this.editingPartner) {
        const p = this.editingPartner;
        if (!this.unlimited.meetings && maxMeetings < (p.meetingsUsed || 0)) {
          this.formError = `Không thể giảm Max Meetings xuống dưới số đã dùng (${p.meetingsUsed}).`;
          return null;
        }
        if (!this.unlimited.webinars && maxWebinars < (p.webinarsUsed || 0)) {
          this.formError = `Không thể giảm Max Webinars xuống dưới số đã dùng (${p.webinarsUsed}).`;
          return null;
        }
        if (!this.unlimited.users && maxUsers < (p.currentUsers || 0)) {
          this.formError = `Không thể giảm Max Users xuống dưới số đang dùng (${p.currentUsers}).`;
          return null;
        }
      }
      const body = {
        partnerName: this.form.partnerName.trim(),
        bundleIds,
        features: this.form.features,
        enabledUtilities: this.form.enabledUtilities || [],
        expiresAt: this.$moment(this.form.expiresAt).toISOString(),
        maxUsers,
        maxMeetings,
        maxWebinars,
      };
      // Chỉ gửi khi có nhập → tạo mới đặt secret; sửa bỏ trống = giữ nguyên.
      if (this.form.accessTokenSecret) body.accessTokenSecret = this.form.accessTokenSecret.trim();
      // NPM_TOKEN + Note: gửi luôn (rỗng = xoá) — hiển thị đúng WYSIWYG.
      body.npmToken = (this.form.npmToken || '').trim();
      body.note = (this.form.note || '').trim();
      body.packageId = this.form.packageId || '';
      return body;
    },
    async submitForm() {
      const body = this.validateForm();
      if (!body) return;
      this.formLoading = true;
      try {
        if (this.formMode === 'create') {
          const res = await SdkPartnerService.createPartner(body);
          this.$bvModal.hide('sdk-partner-form');
          const key = res && (res.apiKey || (res.data && res.data.apiKey));
          if (key) this.showApiKey(key);
          this.$toastr.s({ title: 'Thành công !', msg: 'Tạo partner thành công' });
        } else {
          await SdkPartnerService.updatePartner(this.editingId, body);
          this.$bvModal.hide('sdk-partner-form');
          this.$toastr.s({ title: 'Thành công !', msg: 'Đã lưu thay đổi' });
        }
        await this.loadData();
      } catch (error) {
        this.toastError(error);
      } finally {
        this.formLoading = false;
      }
    },
    resetForm() {
      this.form = this.defaultForm();
      this.formError = '';
      this.editingId = null;
      this.editingPartner = null;
    },
    showApiKey(key) {
      this.apiKey = key;
      this.copied = false;
      this.$bvModal.show('sdk-apikey-dialog');
    },
    clearApiKey() {
      this.apiKey = '';
      this.copied = false;
    },
    async copyKey() {
      try {
        await navigator.clipboard.writeText(this.apiKey);
        this.copied = true;
      } catch (e) {
        this.copied = false;
      }
    },
    confirmRevoke(partner) {
      this.confirm = {
        title: 'Revoke API Key',
        message: `Vô hiệu hóa partner <strong>${partner.partnerName}</strong>? Hành động này có hiệu lực <strong>ngay lập tức</strong> và không thể hoàn tác.`,
        okText: 'Revoke',
        variant: 'danger',
        loading: false,
        action: 'revoke',
        partner,
      };
      this.$bvModal.show('sdk-confirm');
    },
    confirmReactivate(partner) {
      const expired = this.$moment(partner.expiresAt).isBefore(this.$moment());
      if (expired) {
        this.$toastr.e({
          title: 'Không thể kích hoạt',
          msg: 'Key đã hết hạn, cần gia hạn expiresAt trước khi kích hoạt lại.',
          preventDuplicates: true,
        });
        return;
      }
      this.confirm = {
        title: 'Kích hoạt lại Partner',
        message: `Kích hoạt lại partner <strong>${partner.partnerName}</strong> với key hiện tại?`,
        okText: 'Kích hoạt',
        variant: 'success',
        loading: false,
        action: 'reactivate',
        partner,
      };
      this.$bvModal.show('sdk-confirm');
    },
    confirmRotate(partner) {
      this.confirm = {
        title: 'Rotate API Key',
        message: `Key cũ của <strong>${partner.partnerName}</strong> sẽ bị vô hiệu ngay lập tức. Key mới chỉ hiển thị 1 lần duy nhất.`,
        okText: 'Rotate',
        variant: 'warning',
        loading: false,
        action: 'rotate',
        partner,
      };
      this.$bvModal.show('sdk-confirm');
    },
    async runConfirm() {
      const { action, partner } = this.confirm;
      if (!action || !partner) return;
      this.confirm.loading = true;
      try {
        if (action === 'revoke') {
          await SdkPartnerService.revokePartner(partner.id);
          this.$toastr.s({ title: 'Thành công !', msg: 'Đã revoke partner' });
        } else if (action === 'reactivate') {
          await SdkPartnerService.reactivatePartner(partner.id);
          this.$toastr.s({ title: 'Thành công !', msg: 'Đã kích hoạt lại partner' });
        } else if (action === 'rotate') {
          const res = await SdkPartnerService.rotateKey(partner.id);
          const key = res && (res.apiKey || (res.data && res.data.apiKey));
          if (key) this.showApiKey(key);
          this.$toastr.s({ title: 'Thành công !', msg: 'Đã rotate key' });
        }
        this.$bvModal.hide('sdk-confirm');
        await this.loadData();
      } catch (error) {
        this.toastError(error);
      } finally {
        this.confirm.loading = false;
      }
    },
    goLogs(partner) {
      this.$router.push({
        name: 'sdk_partner_logs',
        params: { id: partner.id },
        query: { name: partner.partnerName },
      });
    },
    // Chia sẻ: copy block thông tin tích hợp cần thiết cho đối tác vào clipboard.
    async sharePartner(partner) {
      const text = this.buildShareText(partner);
      const ok = await this.copyToClipboard(text);
      if (ok) {
        this.$toastr.s({
          title: 'Đã sao chép',
          msg: 'Thông tin tích hợp đã được copy vào clipboard — gửi cho đối tác.',
        });
      } else {
        this.$toastr.e({
          title: 'Không sao chép được',
          msg: 'Trình duyệt chặn clipboard — vui lòng copy thủ công.',
          preventDuplicates: true,
        });
      }
    },
    buildShareText(p) {
      const lim = (v) => (v ? v : '∞');
      return [
        'DiaB SDK — Thông tin tích hợp',
        '──────────────────────────────',
        `Partner    : ${p.partnerName || '—'}`,
        `Base URL   : ${SdkPartnerService.baseUrl() || '—'}`,
        `SDK Key    : ${p.apiKeyPrefix || '—'}…  (key đầy đủ chỉ hiển thị 1 lần khi tạo/rotate — không hiển thị lại)`,
        `Bundle ID  : ${(p.bundleIds || []).join(', ') || '—'}`,
        `Tính năng  : ${(p.features || []).join(', ') || '—'}`,
        `Hết hạn    : ${this.formatDate(p.expiresAt)}`,
        `Giới hạn   : Users ${lim(p.maxUsers)} · Meetings ${lim(p.maxMeetings)} · Webinars ${lim(p.maxWebinars)}`,
        `Ký token   : ${p.hasAccessTokenSecret ? 'HS256 — bắt buộc ký token end-user' : 'Không bắt buộc'}`,
        'Gói SDK    : npm i @diabtechapp/pro-diab-sdk',
      ].join('\n');
    },
    async copyToClipboard(text) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch (e) {
        // fall through to legacy path
      }
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.top = '-9999px';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
      } catch (e) {
        return false;
      }
    },
    toastError(error) {
      this.$toastr.e({
        title: 'Lỗi !',
        msg: (error && error.message) || 'Đã có lỗi xảy ra',
        preventDuplicates: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.sdk-unlimited-hint {
  height: calc(1.5em + 1.3rem + 2px);
  display: flex;
  align-items: center;
  padding: 0 0.9rem;
  border: 1px dashed #d7dae3;
  border-radius: 0.42rem;
  background: #f8f9fc;
  color: #8950fc;
  font-weight: 600;
}
.sdk-unlimited-check {
  font-size: 12.5px;
}

.sdk-login-panel {
  max-width: 720px;
  margin: 24px auto;
  padding: 24px;
  border: 1px solid #e4e6ef;
  border-radius: 12px;
  background: #fff;
}

.sdk-summary-bar {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

.sdk-summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid #e4e6ef;
  background: #f8f9fc;

  .sdk-summary-count {
    font-size: 22px;
    font-weight: 700;
  }

  .sdk-summary-label {
    font-size: 12px;
    color: #7e8299;
  }

  &.is-active .sdk-summary-count {
    color: #0bb783;
  }
  &.is-expiring .sdk-summary-count {
    color: #3699ff;
  }
  &.is-expired .sdk-summary-count {
    color: #ffa800;
  }
  &.is-revoked .sdk-summary-count {
    color: #f64e60;
  }
  &.is-requests .sdk-summary-count {
    color: #8950fc;
  }
}

.sdk-apikey-box {
  padding: 12px;
  background: #f3f6f9;
  border-radius: 8px;
  word-break: break-all;

  code {
    font-size: 14px;
    color: #f64e60;
  }
}
</style>
